/**
 * indexUtilizationTooltip.ts
 * Item 6.1 — pure tooltip-text builder for the Index machine's daily %
 * heatmap cell on the lab-wide Machine Schedule / Master Gantt rows.
 *
 * Distinct from `views/scenario/index/indexHeatmapModel.ts`'s
 * `buildDayTooltipLines`: that one is scenario-scoped (splits firmPct into
 * "this plan's own tentative" vs "everything else committed"). This row has
 * no owning scenario — it is the lab-wide BASELINE perspective
 * (`getIndexCapacity(from, to)` with no `scenarioKey`, I13) — so the split
 * that actually matters here is exactly what the day-capacity response
 * already carries: `firmPct` (Committed, any scenario, + BASELINE Tentative —
 * i.e. everything actually locked in) vs `softPct` (other drafts' Tentative,
 * advisory only, I9/I13). Kept as its own small pure module (not a duplicate
 * of the scenario builder — different semantics) so it's unit-testable
 * without mounting the grid.
 */

import type { IndexDayCapacity } from '../services/scheduleIndex';

/** The subset of `IndexDayCapacity` this builder needs (kept narrow for easy test fixtures). */
export type IndexRowTooltipDay = Pick<
  IndexDayCapacity,
  | 'date'
  | 'isWorkingDay'
  | 'firmPct'
  | 'softPct'
  | 'maxTypeFirmPct'
  | 'maxTypeSoftPct'
  | 'isOverCapacity'
  | 'hasUnknownCapacity'
  | 'byType'
>;

/**
 * Plain-text tooltip lines for one day cell of the lab-wide Index-machine row.
 * Line 1 is always the "committed vs draft" headline; byType lines follow.
 */
export function buildIndexRowTooltipLines(day: IndexRowTooltipDay): string[] {
  if (!day.isWorkingDay) {
    return ['Non-working day (weekend or stat holiday) — no index capacity consumed.'];
  }

  // P3 (2026-07-29): each type is its own "machine" — the headline is the
  // BUSIEST type's own %, and each type reports its own share of its own
  // capacity. The cross-type sum is gone (200% from two full types was noise).
  const lines: string[] = [
    `Busiest type: ${day.maxTypeFirmPct}% committed${day.isOverCapacity ? ' — OVER CAPACITY' : ''}`,
    `Busiest type tentative (other draft plans, advisory): ${day.maxTypeSoftPct}%`,
  ];

  if (day.hasUnknownCapacity) {
    lines.push('* one or more types used the default 8/day capacity (not yet set by admin)');
  }

  if (day.byType.length > 0) {
    lines.push('By test type (each vs its own capacity):');
    for (const t of day.byType) {
      const cap = t.capacity != null ? `${t.capacity}/day` : '8/day *';
      lines.push(
        `  ${t.taskTypeName}: ${t.firmPct}%${t.isOverCapacity ? ' — OVER' : ''} (${t.firmCount} committed + ${t.softCount} draft, cap ${cap})`,
      );
    }
  }

  return lines;
}
